/**
 * Where the org's PixelWrangler key is saved (BLU-1399): one line per key
 * target, fed only by `onboarding.getCustomFiles` data (mode, 3PL names and
 * saved booleans; never a key). The ONE copy of this list: the Custom files
 * step and the Product sync Customizer section both render it (CC-1).
 *
 * Imports nothing from `features/product-sync` (N11), so the section's import
 * of this leaf never closes a loop with the onboarding steps.
 */
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { RouterOutputs } from '@/lib/trpc';

type CustomFilesView = RouterOutputs['onboarding']['getCustomFiles'];

/** Renders nothing when there is no key target. */
export function PwSavedList({ view }: { view: CustomFilesView }) {
  const { t } = useTranslation('onboarding');
  const labelId = useId();
  if (view.targets.length === 0) return null;
  return (
    <div className="space-y-1.5">
      <p id={labelId} className="text-xs font-medium text-foreground">
        {t('stepCustomFiles.pw.statusLabel')}
      </p>
      <ul aria-labelledby={labelId} className="space-y-1">
        {view.targets.map((target, i) => {
          const text =
            target.kind === 'self'
              ? target.saved
                ? t('stepCustomFiles.pw.savedSelf')
                : t('stepCustomFiles.pw.notSavedSelf')
              : target.saved
                ? t('stepCustomFiles.pw.savedFor', { name: target.name ?? '' })
                : t('stepCustomFiles.pw.notSavedFor', { name: target.name ?? '' });
          return (
            <li
              key={`${target.kind}-${target.name ?? ''}-${i}`}
              className={target.saved ? 'text-xs text-emerald-700' : 'text-xs text-muted-foreground'}
            >
              {text}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
